import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { ReviewRequirements } from '../tabs/ReviewRequirements.js';

/**
 * THE DETAILED BREAKDOWN WORKFLOW — cast, extras, props, costumes, vehicles,
 * effects, scene by scene (owner brief, 2026-10-29).
 *
 * The same scene cards the Script tab has always had, with only the breakdown
 * half on them: the sheet, tagging words in the text, and each scene's review
 * gate. Nothing here decides a location, and nothing on the Locations pages
 * marks a sheet reviewed: the two workflows share the confirmed scenes and the
 * one AI reading, and nothing else.
 *
 * DONE IS NAVIGATION. Completion is the server's — every scene's sheet marked
 * reviewed — so "Back to workflows" records nothing; the choice screen then
 * shows where this workflow stands, beside Locations.
 */
export function ElementsStep({
  projectId,
  projectTitle,
  onDone,
}: {
  projectId: string;
  projectTitle: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ['current-script', projectId],
    queryFn: () => api.currentScript(projectId),
  });

  if (current.isPending) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="elements-step-loading">
        Loading the screenplay…
      </p>
    );
  }

  const script = current.data;

  // The route redirects before the scenes are confirmed, so this is a URL typed
  // against a production that lost its screenplay — say so.
  if (!script) {
    return (
      <p className="mm" style={{ fontSize: 13 }} data-testid="elements-step-empty">
        There is no screenplay to break down yet.
      </p>
    );
  }

  return (
    <div data-testid="elements-step">
      <ReviewRequirements
        projectId={projectId}
        scriptVersionId={script.scriptVersionId}
        projectTitle={projectTitle}
        scriptVersionLabel={`V${String(script.versionNumber)}`}
        focus="ELEMENTS"
        continueLabel="Back to workflows"
        onContinueToMatching={() => {
          // The choice reads the server's answer; make sure it is this one.
          void queryClient.invalidateQueries({ queryKey: ['project-setup', projectId] }).then(onDone);
        }}
      />
    </div>
  );
}
